import type { MandatoryShotRow } from './types';
import { ulid } from './ulid';

/** The pre-0031 identity: time, location and text, truncated to 200
 * characters, plus a photo suffix. Kept so old ticks and old clients resolve. */
export function legacyShotKey(shot: MandatoryShotRow): string {
  const key = [shot.time ?? '', shot.location ?? '', shot.description ?? ''].join('|').slice(0, 200);
  return shot.media === 'photo' ? `${key}|media:photo` : key;
}

/** The check identity: the permanent ID, or the legacy key for a row that
 * somehow arrives without one. */
export function shotId(shot: MandatoryShotRow): string {
  return shot.id || legacyShotKey(shot);
}

const ULID = /^[0-7][0-9A-HJKMNP-TV-Z]{25}$/;

/** Legacy rows use a deterministic 128-bit hash encoded as a ULID. */
function legacyShotId(sheetId: string, key: string, duplicate: number): string {
  let hash = 0x6c62272e07bb014262b821756295c58dn;
  for (const byte of new TextEncoder().encode(JSON.stringify([sheetId, key, duplicate]))) {
    hash = ((hash ^ BigInt(byte)) * 0x1000000000000000000013bn) & ((1n << 128n) - 1n);
  }
  let id = '';
  for (let i = 0; i < 26; i++) {
    id = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'[Number(hash & 31n)] + id;
    hash >>= 5n;
  }
  return id;
}

/** Give every row a permanent ID. Rows that already carry one keep it, and
 * the rest are derived from the sheet, the legacy key and the duplicate index,
 * so two reads before a save agree. When `previous` is given (an AI reply about
 * that document), a row without an ID first takes the unclaimed ID of a
 * previous row with the same legacy key, so a reply that drops IDs still keeps
 * ticks on the shots it left unchanged. A repeated ID stays with the row whose
 * legacy key matches its previous holder, otherwise with the first row. */
export function assignShotIds(
  rows: MandatoryShotRow[],
  sheetId: string,
  previous: MandatoryShotRow[] = [],
): MandatoryShotRow[] {
  const used = new Set(rows.flatMap((row) => row.id && ULID.test(row.id) ? [row.id] : []));
  const inherited = new Map<string, string[]>();
  for (const row of previous) {
    if (!row.id || !ULID.test(row.id) || used.has(row.id)) continue;
    const key = legacyShotKey(row);
    inherited.set(key, [...(inherited.get(key) ?? []), row.id]);
  }
  const previousKey = new Map(previous.flatMap((row) =>
    row.id && ULID.test(row.id) ? [[row.id, legacyShotKey(row)] as const] : []));
  const owner = new Map<string, number>();
  rows.forEach((row, index) => {
    if (!row.id || !ULID.test(row.id)) return;
    const current = owner.get(row.id);
    const heldKey = previousKey.get(row.id);
    if (current === undefined
      || (legacyShotKey(rows[current]) !== heldKey && legacyShotKey(row) === heldKey)) owner.set(row.id, index);
  });
  const occurrences = new Map<string, number>();
  return rows.map((row, position) => {
    const key = legacyShotKey(row);
    let index = occurrences.get(key) ?? 0;
    occurrences.set(key, index + 1);
    if (row.id && owner.get(row.id) === position) return row;
    const reuse = inherited.get(key)?.shift();
    if (reuse && !used.has(reuse)) {
      used.add(reuse);
      return { ...row, id: reuse };
    }
    let id = legacyShotId(sheetId, key, index);
    while (used.has(id)) id = legacyShotId(sheetId, key, ++index);
    used.add(id);
    return { ...row, id };
  });
}

/** Fresh random IDs for rows that are new by definition (drafts, new sheets). */
export function newShotIds(rows: MandatoryShotRow[]): MandatoryShotRow[] {
  return rows.map((row) => ({ ...row, id: ulid() }));
}
